import { useQuery } from "@tanstack/react-query";
import { fetchGeoCountry } from "@majourneys/currency";
import { DEFAULT_GUEST_NATIONALITY } from "@majourneys/shared";

/**
 * The visitor's country, for use as `guestNationality` on a stay search.
 *
 * WHY IT EXISTS. LiteAPI gates and prices inventory by market, and every
 * search we made sent a hard-coded "MA". A French guest was therefore shopping
 * the Moroccan-resident market and could be quoted a rate their own market is
 * not sold — which the supplier only refuses later, at prebook, after they
 * have chosen a room.
 *
 * WHY THE EDGE SIGNAL AND NOT THE GUEST. Checkout never asks for a
 * nationality, so there is nothing truer to read. Cloudflare's country says
 * where the visitor is, not who they are: wrong for an expat, absent behind a
 * VPN or in local dev. Both cases return null and the API falls back to MA,
 * which is precisely the behaviour that shipped before this existed — so the
 * worst outcome here is the old one.
 *
 * NEVER READ DURING SSR. `/__geo` is `no-store` for a reason: the HTML this
 * app serves is cached at the Cloudflare edge for five minutes and shared
 * between visitors, so a nationality baked into a server render would hand
 * the first visitor's market to everyone who followed. The country is fetched
 * in the browser only, and the query keys that carry it are null until dates
 * are chosen — see `nationalityKey` in features/stays/api/stays-query.ts.
 */
export function useGuestNationality(): string | undefined {
  const { data } = useQuery({
    queryKey: ["geo", "country"],
    queryFn: () => fetchGeoCountry(),
    // The visitor's country cannot change mid-visit in any way worth a second
    // request, and this runs on every stays page.
    staleTime: Infinity,
    gcTime: Infinity,
    // A geo probe that fails is a geo probe we do without: the API's own
    // default covers it, and retrying would only delay the price.
    retry: false,
    // Nothing to fetch on the server, and fetching there would be wrong —
    // see the note about the edge HTML cache above.
    enabled: typeof window !== "undefined",
  });

  const code = data?.trim().toUpperCase();
  // Anything that is not a plain two-letter code is dropped rather than
  // forwarded: the API would only fall back to MA anyway, and sending it
  // would fragment the query cache on a value that changes no price.
  if (!code || !/^[A-Z]{2}$/.test(code)) return undefined;
  // Already the default — sending it changes nothing and would give Moroccan
  // visitors a second cache entry holding an identical body.
  return code === DEFAULT_GUEST_NATIONALITY ? undefined : code;
}
